/**
 * Footer newsletter email capture.
 * RHF + Zod validation; submits to BFF POST /v1/storefront/leads.
 */

'use client';

import styled from '@emotion/styled';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button, FormField, Input, Text } from '@template-project/afrodita-components';
import { useTranslation } from '@/lib/i18n';
import {
  NEWSLETTER_ERROR_GENERIC,
  NEWSLETTER_ERROR_INVALID,
  NEWSLETTER_PLACEHOLDER,
  NEWSLETTER_SUBSCRIBE,
  NEWSLETTER_SUCCESS,
  NEWSLETTER_TITLE,
} from './i18.keys';
import { useIntl } from '@/lib/intl';
import { trackNewsletterSignup } from '@/lib/analytics';
import { hashEmail } from '@/lib/newsletter/hash-email';
import {
  hasRecentNewsletterSignup,
  markNewsletterSignup,
} from '@/lib/newsletter/rate-limit-cookie';
import { subscribeToNewsletter } from '@/infrastructure/services/newsletter.service';
import { newsletterResolver } from './newsletter.resolver';
import type { NewsletterFormData } from './newsletter.schema';

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.md};
  margin-bottom: ${({ theme }) => theme.space['2xl']};
  padding-bottom: ${({ theme }) => theme.space.xl};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.color};

  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    margin-bottom: ${({ theme }) => theme.space.xl};
    padding-bottom: ${({ theme }) => theme.space.lg};
  }
`;

const Title = styled(Text)`
  margin: 0;
`;

const FormRow = styled.form`
  display: flex;
  gap: ${({ theme }) => theme.space.sm};
  align-items: flex-start;
  max-width: 480px;

  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    flex-direction: column;
    max-width: none;
  }
`;

const FieldWrap = styled.div`
  flex: 1;
  min-width: 0;
  width: 100%;
`;

const SubmitButton = styled(Button)`
  flex-shrink: 0;
  white-space: nowrap;

  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    width: 100%;
  }
`;

const SuccessMessage = styled(Text)`
  margin: 0;
  color: ${({ theme }) => theme.colors.semantic.positive.color};
`;

export function NewsletterSignup() {
  const { t } = useTranslation('common');
  const { locale } = useIntl();
  const [succeeded, setSucceeded] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<NewsletterFormData>({
    resolver: newsletterResolver,
    defaultValues: { email: '' },
  });

  const onSubmit = handleSubmit(async ({ email }) => {
    setSubmitError(null);
    const emailHash = await hashEmail(email);

    if (hasRecentNewsletterSignup(emailHash)) {
      setSucceeded(true);
      return;
    }

    try {
      await subscribeToNewsletter(email, locale);
      markNewsletterSignup(emailHash);
      trackNewsletterSignup({ emailHash });
      setSucceeded(true);
    } catch {
      setSubmitError(t(NEWSLETTER_ERROR_GENERIC));
    }
  });

  if (succeeded) {
    return (
      <Section aria-live="polite">
        <Title variant="labelCaps">{t(NEWSLETTER_TITLE)}</Title>
        <SuccessMessage variant="body">{t(NEWSLETTER_SUCCESS)}</SuccessMessage>
      </Section>
    );
  }

  const fieldError =
    errors.email?.message != null
      ? t(NEWSLETTER_ERROR_INVALID)
      : submitError ?? undefined;

  return (
    <Section>
      <Title variant="labelCaps">{t(NEWSLETTER_TITLE)}</Title>
      <FormRow onSubmit={onSubmit} noValidate>
        <FieldWrap>
          <FormField
            htmlFor="footer-newsletter-email"
            error={fieldError}
          >
            <Input
              id="footer-newsletter-email"
              type="email"
              autoComplete="email"
              placeholder={t(NEWSLETTER_PLACEHOLDER)}
              disabled={isSubmitting}
              aria-label={t(NEWSLETTER_PLACEHOLDER)}
              {...register('email')}
            />
          </FormField>
        </FieldWrap>
        <SubmitButton
          type="submit"
          variant="primary"
          loading={isSubmitting}
          loadingLabel={t(NEWSLETTER_SUBSCRIBE)}
          disabled={isSubmitting}
        >
          {t(NEWSLETTER_SUBSCRIBE)}
        </SubmitButton>
      </FormRow>
    </Section>
  );
}
